<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { AlertCircleIcon, PencilEdit02Icon } from '@hugeicons/core-free-icons';
import { useAlert } from 'dashboard/composables';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAutomation } from 'dashboard/composables/useAutomation';
import { useEditableAutomation } from 'dashboard/composables/useEditableAutomation';
import AutomationRuleForm from './AutomationRuleForm.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import FormInput from 'v3/components/Form/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import { validateAutomation } from 'dashboard/helper/validations';
import { AUTOMATION_ACTION_TYPES_ALL } from './constants';
import {
  getWorkflowActionNodeTitle,
  getWorkflowActionStepVisual,
  getWorkflowTriggerStepVisual,
} from './workflowCanvasStepTheme';
import {
  createWorkflowStepId,
  toDateTimeWindowPayload,
} from './dateTimeWindow';

const START_VALUE = {
  name: 'Untitled workflow',
  description: '',
  active: false,
  event_name: '',
  conditions: [],
  actions: [],
};

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const allCustomAttributes = useMapGetter('attributes/getAttributes');

const {
  automation,
  automationTypes,
  onEventChange,
  getConditionDropdownValues,
  appendNewCondition,
  insertActionAfter,
  removeFilter,
  removeAction,
  resetAction,
  getActionDropdownValues,
  manifestCustomAttributes,
} = useAutomation(structuredClone(START_VALUE));

const { formatAutomation } = useEditableAutomation();

const accountId = computed(() => getters.getCurrentAccountId.value);
const records = computed(() => getters['automations/getAutomations'].value);
const uiFlags = computed(() => getters['automations/getUIFlags'].value);

const isSLAEnabled = computed(() =>
  getters['accounts/isFeatureEnabledonAccount'].value(accountId.value, 'sla')
);

const isCreate = computed(() => route.name === 'automation_editor_new');

const editorInstanceKey = computed(() =>
  isCreate.value ? 'new' : String(route.params.automationId ?? '')
);

const listRoute = computed(() => ({
  name: 'automation_list',
  params: { accountId: route.params.accountId },
}));

const savedAutomationSnapshot = ref('');

const normalizeOptionValue = value => {
  if (Array.isArray(value)) {
    return value.map(item => normalizeOptionValue(item));
  }

  if (value && typeof value === 'object') {
    if ('id' in value) {
      return value.id;
    }

    if ('operation' in value && 'tags' in value) {
      return {
        operation: value.operation,
        tags: normalizeOptionValue(value.tags),
      };
    }

    if (
      'operator' in value &&
      ('start_date' in value || 'start_at' in value) &&
      ('end_date' in value || 'end_at' in value)
    ) {
      return toDateTimeWindowPayload(value);
    }

    return {
      team_id: normalizeOptionValue(value.team_id),
      assignment_logic: value.assignment_logic ?? 'round_robin',
      only_online_users: Boolean(value.only_online_users),
      limit_open_contacts_enabled: Boolean(value.limit_open_contacts_enabled),
      max_open_contacts: value.max_open_contacts ?? 20,
      add_timeout_branch: Boolean(value.add_timeout_branch),
      timeout_value: value.timeout_value ?? 7,
      timeout_unit: value.timeout_unit ?? 'days',
    };
  }

  return value;
};

function getAutomationSnapshot(value) {
  if (!value?.conditions || !value?.actions) return '';

  const normalizedConditions = value.conditions.map((condition, index) => ({
    attribute_key: condition.attribute_key ?? '',
    filter_operator: condition.filter_operator ?? '',
    values: normalizeOptionValue(condition.values ?? []),
    query_operator:
      index === value.conditions.length - 1
        ? null
        : (condition.query_operator ?? 'and'),
    custom_attribute_type: condition.custom_attribute_type ?? '',
  }));

  const normalizedActions = value.actions.map(action => ({
    id: action.id ?? null,
    action_name: action.action_name ?? '',
    title: action.title ?? '',
    action_params: normalizeOptionValue(action.action_params ?? []),
    next_step_id: action.next_step_id ?? null,
  }));

  return JSON.stringify({
    id: value.id ?? null,
    name: value.name ?? '',
    description: value.description ?? '',
    event_name: value.event_name ?? '',
    conditions: normalizedConditions,
    actions: normalizedActions,
    active: Boolean(value.active),
  });
}

function markAutomationAsSaved(value) {
  savedAutomationSnapshot.value = getAutomationSnapshot(value);
}

const ensureWorkflowActionIds = value => ({
  ...value,
  actions: (value.actions || []).map(action => ({
    ...action,
    id: action.id || createWorkflowStepId(),
    next_step_id: action.next_step_id || null,
  })),
});

const syncFormStateFromRoute = () => {
  manifestCustomAttributes();
  if (isCreate.value) {
    const name = String(route.query.name || START_VALUE.name).trim();
    const description = String(route.query.description || '').trim();
    automation.value = ensureWorkflowActionIds({
      ...structuredClone(START_VALUE),
      name: name || START_VALUE.name,
      description,
    });
    markAutomationAsSaved(automation.value);
    return;
  }
  const id = Number(route.params.automationId);
  const record = records.value.find(r => r.id === id);
  if (record?.deprecated_trigger) {
    useAlert(t('AUTOMATION.DEPRECATED_WORKFLOW'));
    router.replace(listRoute.value);
    return;
  }
  if (record?.conditions) {
    automation.value = ensureWorkflowActionIds(
      formatAutomation(
        record,
        allCustomAttributes.value,
        automationTypes,
        AUTOMATION_ACTION_TYPES_ALL
      )
    );
    markAutomationAsSaved(automation.value);
  }
};

watch(
  () => [route.name, route.params.automationId, records.value],
  () => {
    syncFormStateFromRoute();
  },
  { immediate: true }
);

onMounted(async () => {
  await store.dispatch('inboxes/get');
  await store.dispatch('agents/get');
  await store.dispatch('contacts/get');
  await store.dispatch('teams/get');
  await store.dispatch('aiAgents/get');
  await store.dispatch('labels/get');
  await store.dispatch('campaigns/get');
  await store.dispatch('automations/get');
  if (isSLAEnabled.value) {
    await store.dispatch('sla/get');
  }
  syncFormStateFromRoute();
});

const isReady = computed(() => {
  if (isCreate.value) return Boolean(automation.value?.conditions);
  const id = Number(route.params.automationId);
  if (Number.isNaN(id)) return false;
  const record = records.value.find(r => r.id === id);
  if (!record) return false;
  return Number(automation.value?.id) === id;
});

const notFound = computed(() => {
  if (isCreate.value) return false;
  if (uiFlags.value.isFetching) return false;
  const id = Number(route.params.automationId);
  if (Number.isNaN(id)) return true;
  return !records.value.some(r => r.id === id);
});

const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const isReadOnly = computed(
  () => !isCreate.value && Boolean(automation.value?.active)
);

const validationPopoverOpen = ref(false);
const selectedValidationTarget = ref('');
const unsavedChangesModalRef = ref(null);
const skipUnsavedChangesGuard = ref(false);

const currentValidationErrors = computed(() =>
  automation.value ? validateAutomation(automation.value) : {}
);

const hasUnsavedChanges = computed(() => {
  if (!automation.value) return false;
  return (
    getAutomationSnapshot(automation.value) !== savedAutomationSnapshot.value
  );
});

const validationIssueItems = computed(() => {
  const errors = currentValidationErrors.value;
  const items = [];

  if (errors.name || errors.description) {
    items.push({
      key: 'details',
      label: t('AUTOMATION.FLOW.PANEL_DETAILS'),
      iconClass: 'i-lucide-file-warning text-n-amber-10',
      target: 'meta',
    });
  }

  if (
    errors.event_name ||
    Object.keys(errors).some(key => key.startsWith('condition_'))
  ) {
    const triggerVisual = getWorkflowTriggerStepVisual();
    items.push({
      key: 'trigger',
      label: t('AUTOMATION.FLOW.PANEL_TRIGGER_CONDITIONS'),
      icon: triggerVisual.hugeIcon,
      iconClass: triggerVisual.iconClass,
      target: 'trigger-conditions',
    });
  }

  (automation.value?.actions || []).forEach((action, index) => {
    if (!errors[`action_${index}`]) return;
    const visual = getWorkflowActionStepVisual(action.action_name);
    items.push({
      key: `action-${index}`,
      label: getWorkflowActionNodeTitle(
        action.action_name,
        automation.value.actions,
        index,
        t
      ),
      icon: visual.hugeIcon,
      iconClass: visual.iconClass,
      target: `action-${index}`,
    });
  });

  return items;
});

const hasValidationIssues = computed(
  () => !isReadOnly.value && validationIssueItems.value.length > 0
);

const closeValidationPopover = () => {
  validationPopoverOpen.value = false;
};

const toggleValidationPopover = () => {
  if (!hasValidationIssues.value) return;
  validationPopoverOpen.value = !validationPopoverOpen.value;
};

const goBack = () => {
  router.push(listRoute.value);
};

const confirmDiscardUnsavedChanges = async () => {
  if (!hasUnsavedChanges.value || skipUnsavedChangesGuard.value) {
    return true;
  }

  return unsavedChangesModalRef.value?.showConfirmation?.();
};

const beforeUnloadHandler = event => {
  if (!hasUnsavedChanges.value || skipUnsavedChangesGuard.value) {
    return;
  }

  event.preventDefault();
  event.returnValue = '';
};

onMounted(() => {
  window.addEventListener('beforeunload', beforeUnloadHandler);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', beforeUnloadHandler);
});

onBeforeRouteLeave(async () => {
  return confirmDiscardUnsavedChanges();
});

onBeforeRouteUpdate(async () => {
  return confirmDiscardUnsavedChanges();
});

const submitAutomation = async (payload, mode, meta = {}) => {
  const { stayOnPage = false, forceActive } = meta;
  const body = { ...payload };
  if (typeof forceActive === 'boolean') {
    body.active = forceActive;
  }

  try {
    const successMessage = (() => {
      if (stayOnPage) return t('AUTOMATION.EDITOR.SAVE_SUCCESS');
      if (mode === 'edit') return t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE');
      return t('AUTOMATION.ADD.API.SUCCESS_MESSAGE');
    })();

    let result;
    if (mode === 'edit') {
      result = await store.dispatch('automations/update', {
        id: body.id,
        ...body,
      });
    } else {
      result = await store.dispatch('automations/create', body);
    }

    useAlert(successMessage, stayOnPage ? { variant: 'success' } : null);
    markAutomationAsSaved(result || body);

    if (stayOnPage) {
      if (mode === 'create' && result?.id) {
        skipUnsavedChangesGuard.value = true;
        await router.replace({
          name: 'automation_editor_edit',
          params: {
            accountId: route.params.accountId,
            automationId: String(result.id),
          },
        });
        skipUnsavedChangesGuard.value = false;
        await store.dispatch('automations/get');
        syncFormStateFromRoute();
      } else if (
        mode === 'edit' &&
        result &&
        typeof result.active === 'boolean'
      ) {
        await store.dispatch('automations/get');
        syncFormStateFromRoute();
      }
    } else {
      skipUnsavedChangesGuard.value = true;
      await router.push(listRoute.value);
      skipUnsavedChangesGuard.value = false;
    }
  } catch (error) {
    skipUnsavedChangesGuard.value = false;
    const errorMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.ERROR_MESSAGE')
        : t('AUTOMATION.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const automationFormRef = ref(null);

const saveFromTopbar = () => {
  if (
    isReadOnly.value ||
    hasValidationIssues.value ||
    !hasUnsavedChanges.value
  ) {
    return;
  }
  automationFormRef.value?.requestSave?.({ stayOnPage: true });
};

const publishFromTopbar = () => {
  if (isReadOnly.value || hasValidationIssues.value) return;
  automationFormRef.value?.requestSave?.({
    stayOnPage: true,
    forceActive: true,
  });
};

const stopFromTopbar = async () => {
  if (isCreate.value || !automation.value?.id || isSaving.value) return;

  try {
    await store.dispatch('automations/update', {
      id: automation.value.id,
      active: false,
    });
    await store.dispatch('automations/get');
    syncFormStateFromRoute();
    markAutomationAsSaved(automation.value);
    useAlert(t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  }
};

const metaDialogRef = ref(null);
const draftName = ref('');
const draftDescription = ref('');
const modalNameError = ref('');
const modalDescError = ref('');

const displayTitle = computed(() => {
  const name = automation.value?.name;
  if (name && String(name).trim()) return String(name).trim();
  return t('AUTOMATION.FLOW.DETAILS_PLACEHOLDER');
});

const displayDescription = computed(() => {
  const desc = automation.value?.description;
  if (desc && String(desc).trim()) return String(desc).trim();
  return t('AUTOMATION.EDITOR.DESCRIPTION_PLACEHOLDER');
});

const openMetaModal = () => {
  draftName.value = automation.value?.name || '';
  draftDescription.value = automation.value?.description || '';
  modalNameError.value = '';
  modalDescError.value = '';
  metaDialogRef.value?.open();
};

const openValidationIssueTarget = item => {
  selectedValidationTarget.value = item.target;
  closeValidationPopover();
  if (item.target === 'meta') {
    openMetaModal();
    return;
  }
  automationFormRef.value?.openPanel?.(item.target);
};

const onMetaDialogClose = () => {
  modalNameError.value = '';
  modalDescError.value = '';
};

const applyMetaFromModal = () => {
  modalNameError.value = '';
  modalDescError.value = '';
  const name = String(draftName.value || '').trim();
  const desc = String(draftDescription.value || '').trim();
  if (!name) {
    modalNameError.value = t('AUTOMATION.ADD.FORM.NAME.ERROR');
  }
  if (!desc) {
    modalDescError.value = t('AUTOMATION.ADD.FORM.DESC.ERROR');
  }
  if (modalNameError.value || modalDescError.value) return;
  automation.value.name = name;
  automation.value.description = desc;
  automationFormRef.value?.resetValidation?.();
  metaDialogRef.value?.close();
};

const onValidationFailed = errs => {
  if (errs?.actions) {
    useAlert(t('AUTOMATION.ERRORS.ATLEAST_ONE_ACTION_REQUIRED'));
    return;
  }

  if (!errs || (!errs.name && !errs.description)) return;
  draftName.value = automation.value?.name || '';
  draftDescription.value = automation.value?.description || '';
  modalNameError.value = errs.name ? t('AUTOMATION.ADD.FORM.NAME.ERROR') : '';
  modalDescError.value = errs.description
    ? t('AUTOMATION.ADD.FORM.DESC.ERROR')
    : '';
  metaDialogRef.value?.open();
};
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden"
  >
    <woot-loading-state
      v-if="!isCreate && uiFlags.isFetching && !records.length"
      :message="$t('AUTOMATION.LOADING')"
    />
    <p
      v-else-if="notFound"
      class="py-16 text-center text-body-main text-n-slate-11"
    >
      {{ $t('AUTOMATION.EDITOR.NOT_FOUND') }}
    </p>
    <template v-else-if="isReady && automation">
      <div
        class="mb-0 flex h-12 w-full shrink-0 items-center justify-between border-b border-n-weak bg-n-solid-2 px-4 dark:border-n-strong"
      >
        <div class="flex w-1/2 min-w-0 items-center gap-2">
          <BackButton :back-url="listRoute" icon-only class="shrink-0" />
          <div class="flex min-w-0 shrink items-center gap-1">
            <div class="flex min-w-0 flex-col">
              <h1
                class="m-0 truncate text-sm font-semibold leading-tight text-n-slate-12"
              >
                {{ displayTitle }}
              </h1>
              <p class="m-0 truncate text-xs leading-tight text-n-slate-11">
                {{ displayDescription }}
              </p>
            </div>
            <NextButton
              v-if="!isReadOnly"
              ghost
              slate
              sm
              class="shrink-0 !bg-transparent hover:!bg-transparent focus:!bg-transparent active:!bg-transparent shadow-none"
              :aria-label="$t('AUTOMATION.EDITOR.TOPBAR_EDIT')"
              :title="$t('AUTOMATION.EDITOR.TOPBAR_EDIT')"
              @click="openMetaModal"
            >
              <template #icon>
                <HugeIcon :icon="PencilEdit02Icon" :size="22" />
              </template>
            </NextButton>
          </div>
        </div>
        <div class="flex w-1/2 shrink-0 items-center justify-end gap-2">
          <div
            v-if="hasValidationIssues"
            v-on-click-outside="closeValidationPopover"
            class="relative flex shrink-0"
          >
            <button
              type="button"
              class="flex size-8 items-center justify-center rounded-lg border border-n-amber-8/40 bg-transparent text-n-amber-10 transition-colors hover:bg-n-amber-9/10"
              :aria-label="$t('AUTOMATION.EDITOR.VALIDATION_POPOVER_TITLE')"
              @click="toggleValidationPopover"
            >
              <HugeIcon
                :icon="AlertCircleIcon"
                :size="22"
                class="shrink-0 text-n-amber-10"
              />
            </button>

            <div
              v-if="validationPopoverOpen"
              class="absolute right-0 top-full z-50 mt-2 flex w-72 flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-lg dark:border-n-strong dark:bg-n-solid-2"
            >
              <div
                class="border-b border-n-weak px-4 py-3 text-sm font-medium text-n-slate-12 dark:border-n-strong"
              >
                {{ $t('AUTOMATION.EDITOR.VALIDATION_POPOVER_TITLE') }}
              </div>
              <ul class="m-0 list-none p-2">
                <li
                  v-for="item in validationIssueItems"
                  :key="item.key"
                  class="rounded-lg"
                >
                  <button
                    type="button"
                    class="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 active:bg-n-alpha-2 dark:hover:bg-n-alpha-3 dark:focus-visible:bg-n-alpha-3 dark:active:bg-n-alpha-3"
                    :class="
                      selectedValidationTarget === item.target
                        ? 'bg-n-alpha-2 dark:bg-n-alpha-3'
                        : ''
                    "
                    @click="openValidationIssueTarget(item)"
                  >
                    <HugeIcon
                      v-if="item.icon"
                      :icon="item.icon"
                      :size="18"
                      class="shrink-0"
                      :class="item.iconClass"
                    />
                    <span
                      v-else
                      class="inline-flex size-5 shrink-0 items-center justify-center"
                      :class="item.iconClass"
                      aria-hidden="true"
                    />
                    <span class="min-w-0 truncate">{{ item.label }}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <NextButton
            v-if="!isReadOnly"
            outline
            blue
            sm
            class="!h-8 !min-w-[4.75rem] !rounded-lg !bg-transparent !px-4 !text-[15px] !font-medium hover:!bg-n-brand/10 focus-visible:!bg-n-brand/10"
            :label="$t('AUTOMATION.EDITOR.SAVE')"
            :is-loading="isSaving"
            :disabled="isSaving || hasValidationIssues || !hasUnsavedChanges"
            @click="saveFromTopbar"
          />
          <NextButton
            v-if="!isReadOnly"
            solid
            blue
            sm
            class="!h-8 !min-w-[4.75rem] !rounded-lg !px-4 !text-[15px] !font-medium hover:!brightness-105 focus-visible:!brightness-105"
            :label="$t('AUTOMATION.EDITOR.PUBLISH')"
            :is-loading="isSaving"
            :disabled="isSaving || hasValidationIssues"
            @click="publishFromTopbar"
          />
          <NextButton
            v-else
            solid
            ruby
            sm
            class="!h-8 !min-w-[4.75rem] !rounded-lg !px-4 !text-[15px] !font-medium"
            label="Stop"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="stopFromTopbar"
          />
        </div>
      </div>
      <div class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <AutomationRuleForm
          :key="editorInstanceKey"
          ref="automationFormRef"
          v-model:automation="automation"
          :mode="isCreate ? 'create' : 'edit'"
          :automation-types="automationTypes"
          :get-condition-dropdown-values="getConditionDropdownValues"
          :get-action-dropdown-values="getActionDropdownValues"
          :append-new-condition="appendNewCondition"
          :insert-action-after="insertActionAfter"
          :remove-filter="removeFilter"
          :remove-action="removeAction"
          :reset-action="resetAction"
          :on-event-change="onEventChange"
          :is-read-only="isReadOnly"
          @save="submitAutomation"
          @cancel="goBack"
          @validation-failed="onValidationFailed"
        />
      </div>
    </template>

    <Dialog
      ref="metaDialogRef"
      type="edit"
      :title="t('AUTOMATION.EDITOR.META_MODAL_TITLE')"
      width="lg"
      :confirm-button-label="t('AUTOMATION.EDITOR.META_SAVE')"
      :show-cancel-button="false"
      @confirm="applyMetaFromModal"
      @close="onMetaDialogClose"
    >
      <div class="flex w-full flex-col gap-4">
        <div class="flex flex-col gap-1">
          <FormInput
            v-model="draftName"
            name="automation-meta-name"
            spacing="compact"
            :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
            :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
            :has-error="Boolean(modalNameError)"
            :error-message="modalNameError"
          />
        </div>
        <TextArea
          id="automation-meta-description"
          v-model="draftDescription"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
          :message="modalDescError"
          :message-type="modalDescError ? 'error' : 'info'"
          resize
          min-height="5rem"
        />
      </div>
    </Dialog>
    <ConfirmationModal
      ref="unsavedChangesModalRef"
      :title="$t('AUTOMATION.EDITOR.UNSAVED_CHANGES_TITLE')"
      :description="$t('AUTOMATION.EDITOR.UNSAVED_CHANGES_MESSAGE')"
      :confirm-label="$t('AUTOMATION.EDITOR.UNSAVED_CHANGES_CONFIRM')"
      :cancel-label="$t('AUTOMATION.EDITOR.UNSAVED_CHANGES_CANCEL')"
    />
  </div>
</template>
